/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import InfiniteScroll from 'react-infinite-scroller';
import { toast } from 'react-toastify';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { FormControlLabel, Checkbox } from '@mui/material';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ptBR } from 'date-fns/locale';

import api from 'api';
import { Header, SideMenuBroker, QuotationItem, Button, Select, Dialog } from 'components';
import { Container, ContentArea, Title, Div, DataArea, ListHeader, LoadingArea, CountArea, ModalButton } from './styles';
import { TiFilter } from 'react-icons/ti';
import { loadingColor, primary, white } from 'styles/colorProvider';
import { Search } from 'components/index';

const MAX_DATE_RANGE_DAYS = 31;
const validateDateRange = (startStr, endStr) => {
  if (!startStr || !endStr || startStr.length < 10 || endStr.length < 10) return { valid: true };
  const start = moment(startStr, 'DD/MM/YYYY', true);
  const end = moment(endStr, 'DD/MM/YYYY', true);
  if (!start.isValid()) return { valid: false, error: 'Data inicial inválida. Use DD/MM/AAAA.' };
  if (!end.isValid()) return { valid: false, error: 'Data final inválida. Use DD/MM/AAAA.' };
  if (end.isBefore(start)) return { valid: false, error: 'A data final deve ser igual ou posterior à data inicial.' };
  if (end.diff(start, 'days') > MAX_DATE_RANGE_DAYS) {
    return { valid: false, error: `O intervalo não pode ser maior que ${MAX_DATE_RANGE_DAYS} dias (1 mês).` };
  }
  return { valid: true };
};

function QuotationList({ history }) {
  const scrollParent = useRef();
  const [search, setSearch] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [quotationsList, setQuotationsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState();
  const [hasNextPage, setHasNextPage] = useState();
  const [userData, setUserData] = useState({});
  const [open, setOpen] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [selectStatus, setSelectStatus] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [dateType, setDateType] = useState('created_at');
  const [startData, setStartData] = useState('');
  const [endData, setEndData] = useState('');
  const [dateRangeError, setDateRangeError] = useState('');
  const [touchFilter, setTouchFilter] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setCurrentPage(1);
    const resp = await api({
      method: 'GET',
      url: `/list-my-quotations`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: Cookies.get('splitriskwl-authtoken'),
      },
      params: {
        page: 1,
        take: 10,
        search,
        owner: selectedOwner,
        status: selectStatus.length ? selectStatus.join(',') : undefined,
        startdate: startData && startData.length >= 10 ? startData : undefined,
        enddate: endData && endData.length >= 10 ? endData : undefined,
        dateType: startData && endData ? dateType : undefined,
      },
      json: true,
    });
    setQuotationsList(resp?.data?.response);
    setTotalCount(resp?.data?.totalCount);
    setHasNextPage(resp?.data?.hasNextPage);
    setLoading(false);
    setTouchFilter(false);
  };

  const getUser = async () => {
    const userInfo = {
      method: 'GET',
      url: `/me`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: Cookies.get('splitriskwl-authtoken'),
      },
      json: true,
    };
    const response = await api(userInfo);

    setUserData(response.data);
  };

  const handleLoadMore = async () => {
    if (touchFilter) return;
    const page = currentPage + 1;
    if (hasNextPage && !loadingMore) {
      setLoadingMore(true);
      const resp = await api({
        method: 'GET',
        url: `/list-my-quotations`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitriskwl-authtoken'),
        },
        params: {
          page,
          take: 10,
          search,
          owner: selectedOwner,
          status: selectStatus.length ? selectStatus.join(',') : undefined,
          startdate: startData && startData.length >= 10 ? startData : undefined,
          enddate: endData && endData.length >= 10 ? endData : undefined,
          dateType: startData && endData ? dateType : undefined,
        },
        json: true,
      });
      const data = quotationsList;
      const ndata = data.concat(resp?.data?.response);
      setCurrentPage(page);
      setQuotationsList(ndata);
      setHasNextPage(resp?.data?.hasNextPage);
      setLoadingMore(false);
    }
  };

  const handleClear = () => {
    setSelectStatus([]);
    setSelectedOwner('all');
    setStartData('');
    setEndData('');
    setDateRangeError('');
  };

  const handleOpenFilter = () => {
    setDateRangeError('');
    setOpen(true);
  };

  const handleDateChange = (field, value) => {
    setDateRangeError('');
    if (field === 'start') setStartData(value || '');
    else setEndData(value || '');
  };

  const parseDateForPicker = (str) => {
    if (!str || String(str).trim().length < 10) return null;
    const m = moment(str, 'DD/MM/YYYY', true);
    return m.isValid() ? m.toDate() : null;
  };

  const statusOptions = [
    { value: 'Expirada', label: 'Expirada' },
    { value: 'Vigente', label: 'Vigente' },
  ];

  const toggleStatus = (value) => {
    setSelectStatus((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const handleExport = async () => {
    if (startData && endData) {
      const { valid, error } = validateDateRange(startData, endData);
      if (!valid) {
        toast.error(error, { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
        return;
      }
    }

    setLoadingExport(true);
    const params = new URLSearchParams();
    params.set('owner', selectedOwner || 'all');
    if (selectStatus.length) params.set('status', selectStatus.join(','));
    if (startData && startData.length >= 10 && endData && endData.length >= 10) {
      if (dateType === 'created_at') {
        params.set('startdate', startData);
        params.set('enddate', endData);
      } else {
        params.set('startdate_expiration', startData);
        params.set('enddate_expiration', endData);
      }
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';

    try {
      const resp = await api({
        method: 'GET',
        url: `/company-quotations-report/stream${queryString}`,
        headers: { Authorization: Cookies.get('splitriskwl-authtoken') },
        responseType: 'blob',
      });

      const blob = resp?.data;
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cotacoes_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download iniciado!', { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
      } else {
        toast.info('Não há dados para exportar', { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
      }
      setOpenExport(false);
    } catch (err) {
      const data = err?.response?.data;
      let message = 'Não foi possível exportar cotações';
      if (err?.response?.status === 400 && data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          if (j?.error) message = j.error;
        } catch (_) {}
      } else if (data?.error) message = data.error;
      toast.error(message, { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
    }
    setLoadingExport(false);
  };

  useEffect(() => {
    getUser();
    loadData();
  }, []);

  const renderFilterDialog = () => (
    <Dialog open={open} onClose={() => setOpen(false)} width="55%" title="Filtrar cotações">
      <Select height="30px" label="Autor da cotação" value={selectedOwner} onChange={(e) => setSelectedOwner(e.target.value)}>
        <option value="all">Todas as cotações</option>
        <option value="myquotations">Minhas cotações</option>
        <option value="teamquotations">Cotações da minha equipe</option>
      </Select>

      <div style={{ marginBottom: 16, marginTop: 12 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Status</label>
        <div style={{ marginLeft: 8, display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
          {statusOptions.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={<Checkbox checked={selectStatus.includes(opt.value)} onChange={() => toggleStatus(opt.value)} />}
              label={opt.label}
              style={{ marginRight: 0 }}
            />
          ))}
        </div>
      </div>

      <Select
        height="30px"
        label="Tipo de data"
        value={dateType}
        onChange={(e) => setDateType(e.target.value)}
        style={{ marginBottom: 12 }}
      >
        <option value="created_at">Data de criação</option>
        <option value="due_at">Data de expiração</option>
      </Select>

      <div style={{ marginTop: 8 }}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
          <div style={{ marginBottom: 16 }}>
            <DatePicker
              label="Data inicial"
              value={parseDateForPicker(startData)}
              onChange={(date) => handleDateChange('start', date ? moment(date).format('DD/MM/YYYY') : '')}
              format="dd/MM/yyyy"
              emptyLabel="DD/MM/AAAA"
              invalidDateMessage="Data inválida"
              inputVariant="outlined"
              fullWidth
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <DatePicker
              label="Data final"
              value={parseDateForPicker(endData)}
              onChange={(date) => handleDateChange('end', date ? moment(date).format('DD/MM/YYYY') : '')}
              format="dd/MM/yyyy"
              emptyLabel="DD/MM/AAAA"
              invalidDateMessage="Data inválida"
              inputVariant="outlined"
              fullWidth
            />
          </div>
        </MuiPickersUtilsProvider>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>
          Intervalo máximo: 1 mês ({MAX_DATE_RANGE_DAYS} dias). Preencha as duas datas para filtrar por período.
        </p>
        {dateRangeError && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#d32f2f' }}>{dateRangeError}</p>}
      </div>

      <ModalButton>
        <Button
          height="35px"
          outlined
          disabled={selectedOwner === 'all' && selectStatus.length === 0 && !startData && !endData}
          onClick={handleClear}
        >
          Limpar filtros
        </Button>
        <Button
          height="35px"
          onClick={() => {
            const { valid, error } = validateDateRange(startData, endData);
            if (startData && endData && !valid) {
              setDateRangeError(error);
              toast.error(error, { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
              return;
            }
            setDateRangeError('');
            setTouchFilter(true);
            loadData();
            setOpen(false);
          }}
        >
          Aplicar alterações
        </Button>
      </ModalButton>
    </Dialog>
  );

  const renderExportDialog = () => (
    <Dialog open={openExport} onClose={() => !loadingExport && setOpenExport(false)} width="50%" title="Exportar cotações">
      <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>
        A exportação usará os mesmos filtros aplicados na listagem.
      </p>
      <Button height="35px" style={{ margin: 0 }} onClick={handleExport} disabled={loadingExport}>
        {loadingExport ? <ReactLoading color={white} height={25} width={25} type="spin" /> : 'Exportar'}
      </Button>
    </Dialog>
  );

  return (
    <>
      <Header />
      <Container>
        {renderFilterDialog()}
        {renderExportDialog()}
        <SideMenuBroker />
        <ContentArea>
          <Div>
            <Title>MINHAS COTAÇÕES</Title>
            <div className="containerTitle">
              <Button title="Exportar" height="30px" style={{ marginRight: -5 }} onClick={() => setOpenExport(true)}>
                <RiFileExcel2Fill size={18} />
              </Button>
              <Button title="Filtrar" height="30px" onClick={handleOpenFilter}>
                <TiFilter size={18} />
              </Button>
              {userData?.actions_active && (
                <Button title="Nova cotação" height="30px" onClick={() => history.push('/cotacao')}>
                  Nova cotação
                </Button>
              )}
              <Search
                placeholder="Buscar por representante"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => {
                  setSearch('');
                  loadData();
                }}
                onConfirm={() => loadData()}
              />
            </div>
          </Div>
          <DataArea ref={scrollParent}>
            {!loading && quotationsList?.length > 0 && (
              <ListHeader>
                <span />
                <span>Cliente</span>
                <span>Autor</span>
                <span>Placa</span>
                <span>Marca</span>
                <span>Modelo</span>
                <span>Valor da cotação</span>
                <span>Criada em</span>
                <span title="1-3 dias verde, 4-6 amarelo, 7+ vermelho">●</span>
                <span>Dias</span>
                <span>Ações</span>
              </ListHeader>
            )}
            <InfiniteScroll
              loadMore={handleLoadMore}
              hasMore={hasNextPage}
              useWindow={false}
              getScrollParent={() => scrollParent?.current}
              initialLoad={false}
              style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
              threshold={10}
            >
              <div style={{ width: '100%', minWidth: 'min(100%, 960px)' }}>
                {loading && (
                  <LoadingArea>
                    <ReactLoading color={loadingColor} height={40} width={40} type="spin" />
                  </LoadingArea>
                )}
                {!loading &&
                  quotationsList?.map((quotation) => (
                    <QuotationItem key={quotation.id} quotation={quotation} actionsActive={userData?.actions_active} />
                  ))}
              </div>
            </InfiniteScroll>

            {!loading && !quotationsList?.length && <p>Nenhuma cotação encontrada</p>}
          </DataArea>
          <CountArea>
            {loadingMore && <ReactLoading color={loadingColor} height={20} width={20} type="spin" />}

            {!loadingMore && !loading && (
              <p style={{ color: primary }}>
                Mostrando {quotationsList?.length} de {totalCount}
              </p>
            )}
          </CountArea>
        </ContentArea>
      </Container>
    </>
  );
}

export default QuotationList;
